import { useMemo } from 'react';

import { Card, Flex, ReCharts, Typography } from '@contiple/antd-ds';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { DailyUsage, DashboardOrganization } from '@/types/dashboard';

const { Text } = Typography;

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe'];

interface UsageChartProps {
  dailyUsage: DailyUsage[];
  organizations: DashboardOrganization[];
}

export default function UsageChart({
  dailyUsage,
  organizations,
}: UsageChartProps) {
  const { t } = useTranslation();

  const hasData = dailyUsage.length > 0;

  const { chartData, orgNames } = useMemo(() => {
    if (!hasData) return { chartData: [], orgNames: [] };

    // 실제 사용량 데이터에 등장하는 조직명
    const present = new Set(
      dailyUsage.flatMap((d) => d.organizations.map((o) => o.organizationName))
    );
    // 조직 순서는 내 조직 현황과 동일하게 organizations(최신 생성순)를 기준으로 정렬 (QA-309)
    const orderedFromList = organizations
      .map((o) => o.organizationName)
      .filter((name) => present.has(name));
    const rest = [...present].filter((name) => !orderedFromList.includes(name));
    const names = [...orderedFromList, ...rest];

    const data = dailyUsage.map((day) => {
      const row: Record<string, string | number> = {
        date: dayjs(day.date).format('MM.DD'),
      };
      for (const org of day.organizations) {
        row[org.organizationName] = org.ticketCount;
      }
      return row;
    });

    return { chartData: data, orgNames: names };
  }, [dailyUsage, hasData, organizations]);

  return (
    <Card
      title={t('dashboard.usageByOrg')}
      style={{
        flex: 2,
        borderRadius: 12,
        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
      }}
      styles={{
        header: {
          padding: '20px 20px 16px',
          minHeight: 0,
          borderBottom: 'none',
        },
        body: { padding: '12px 20px 20px' },
      }}>
      {hasData ? (
        <ReCharts.ResponsiveContainer width="100%" height={300}>
          <ReCharts.LineChart
            data={chartData}
            margin={{ top: 5, right: 24, left: 0, bottom: 0 }}>
            <ReCharts.CartesianGrid strokeDasharray="3 3" />
            <ReCharts.XAxis dataKey="date" />
            <ReCharts.YAxis width={48} />
            <ReCharts.Tooltip />
            <ReCharts.Legend />
            {orgNames.map((name, idx) => (
              <ReCharts.Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={COLORS[idx % COLORS.length]}
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            ))}
          </ReCharts.LineChart>
        </ReCharts.ResponsiveContainer>
      ) : (
        <Flex justify="center" align="center" style={{ height: 300 }}>
          <Text type="secondary">{t('dashboard.emptyOrganization')}</Text>
        </Flex>
      )}
    </Card>
  );
}
